import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Calculator, Clock, CheckCircle, Sparkles, Workflow, ArrowRight, Eye } from "lucide-react";

const ApprovalWorkflowInterface = ({ entries, onBulkAction, bulkActionLoading }) => {
  const { t } = useTranslation();
  const workflowStats = useMemo(() => {
    const pending = entries.filter((e) => !e.is_approved).length;
    const approved = entries.filter((e) => e.is_approved).length;
    const total = entries.length;
    return { pending, approved, total, completion: total > 0 ? (approved / total) * 100 : 0 };
  }, [entries]);

  const workflowSteps = [
    {
      id: 1,
      title: t("payrollUi.workflow.generated"),
      status: "completed",
      count: workflowStats.total,
      icon: Calculator,
      color: "text-green-600",
    },
    {
      id: 2,
      title: t("payrollUi.workflow.validating"),
      status: workflowStats.pending > 0 ? "active" : "completed",
      count: workflowStats.pending,
      icon: Clock,
      color: workflowStats.pending > 0 ? "text-amber-600" : "text-green-600",
    },
    {
      id: 3,
      title: t("payrollUi.workflow.approved"),
      status: workflowStats.completion === 100 ? "completed" : "pending",
      count: workflowStats.approved,
      icon: CheckCircle,
      color: workflowStats.completion === 100 ? "text-green-600" : "text-muted-foreground",
    },
    {
      id: 4,
      title: t("payrollUi.workflow.finalized"),
      status: workflowStats.completion === 100 ? "completed" : "pending",
      count: workflowStats.completion === 100 ? workflowStats.total : 0,
      icon: Sparkles,
      color: workflowStats.completion === 100 ? "text-green-600" : "text-muted-foreground",
    },
  ];

  return (
    <Card className="overflow-hidden">
      <CardHeader style={{ backgroundColor: "var(--lm-s2)", borderBottom: "1px solid var(--lm-border)" }}>
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(94,106,210,0.10)" }}>
            <Workflow className="h-4 w-4" style={{ color: "#5e6ad2" }} />
          </div>
          <div>
            <span>{t("payrollUi.workflow.title")}</span>
            <p className="text-xs font-normal mt-0.5" style={{ color: "var(--lm-ink-3)" }}>
              {t("payrollUi.workflow.progressSummary", { pct: Math.round(workflowStats.completion), pending: workflowStats.pending })}
            </p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-muted-foreground">{t("payrollUi.workflow.overallProgress")}</span>
            <span className="text-sm font-bold text-foreground">{Math.round(workflowStats.completion)}%</span>
          </div>
          <div className="relative h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--lm-border)" }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${workflowStats.completion}%`, backgroundColor: "#5e6ad2" }}
            />
          </div>
        </div>

        <div className="space-y-4 mb-6">
          {workflowSteps.map((step, index) => (
            <div key={step.id} className="flex items-center gap-4 group">
              {index < workflowSteps.length - 1 && <div className="absolute left-8 mt-12 w-0.5 h-8 bg-muted" />}
              <div
                className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-4 transition-all duration-300 ${
                  step.status === "completed"
                    ? "bg-green-950/20 border-green-900/40 group-hover:scale-110"
                    : step.status === "active"
                      ? "bg-amber-950/20 border-amber-900/40 animate-pulse"
                      : "bg-muted/50 border-border"
                }`}
              >
                <step.icon className={`h-5 w-5 ${step.color}`} />
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-foreground group-hover:text-foreground transition-colors">
                    {step.title}
                  </h4>
                  <Badge
                    variant={step.status === "completed" ? "success" : step.status === "active" ? "default" : "secondary"}
                    className="text-xs font-medium"
                  >
                    {t("payrollUi.workflow.payslipsCount", { count: step.count })}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {step.status === "completed" && t("payrollUi.workflow.stepCompleted")}
                  {step.status === "active" && t("payrollUi.workflow.stepActive")}
                  {step.status === "pending" && t("payrollUi.workflow.stepPending")}
                </p>
              </div>
              {step.status === "active" && <ArrowRight className="h-5 w-5 text-amber-500 animate-pulse" />}
            </div>
          ))}
        </div>

        {workflowStats.pending > 0 && (
          <div className="flex gap-3 p-4 bg-primary/5 rounded-xl border border-primary/20">
            <div className="flex-1">
              <p className="font-semibold text-foreground mb-1">{t("payrollUi.workflow.quickActions")}</p>
              <p className="text-sm text-muted-foreground">{t("payrollUi.workflow.needValidation", { count: workflowStats.pending })}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => onBulkAction("approve")}
                disabled={bulkActionLoading}
                className="text-white"
                style={{ backgroundColor: "#27a644" }}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                {t("payrollUi.workflow.approveAll")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onBulkAction("review")}
                disabled={bulkActionLoading}
                className="border-border hover:bg-accent"
              >
                <Eye className="h-4 w-4 mr-1" />
                {t("payrollUi.workflow.review")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApprovalWorkflowInterface;
